import { format } from "date-fns";
import { useState } from "react";

import { Card } from "@/components/card";
import { useTableControl } from "@/components/table-control";
import { Badge } from "@/components/ui/badge";

import { useGetRoomUsageList } from "../api/get-room-usage-list";
import { Timeframe } from "../types/timeframe";
import { generateTimeRange } from "../utils/generate-time-range";

import { RoomSystemUsageTable } from "./room-system-usage-table";
import { TimeframeSelector } from "./timeframe-selector";

type RoomUsageByRoomSystemProps = {
  organisationId?: string;
};

export function RoomUsageByRoomSystem({
  organisationId,
}: RoomUsageByRoomSystemProps) {
  const [timeFrame, setTimeFrame] = useState<Timeframe>("w");
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 10,
    persistInUrl: false,
  });

  const { from, to } = generateTimeRange(timeFrame);
  const {
    data: roomUsage,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetRoomUsageList({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
  });

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Room Usage
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeFrame}
          onChange={(value) => {
            setTimeFrame(value as Timeframe);
            setPagination((prev) => ({
              ...prev,
              pageIndex: 0,
            }));
          }}
        />
      </div>

      <div className="relative">
        <RoomSystemUsageTable
          data={isSuccess ? roomUsage : []}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={1}
          isLoading={isLoading || isFetching}
        />
      </div>
    </Card>
  );
}
